import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import columns from './columns';
import OpenCartCard from './OpenCartCard';
import OpenCartsSearch from './OpenCartsSearch';
import { Button, Table, NoResults } from '../../components/';
import SortOptions from '~COMPONENTS/sortOptions/SortOptions';
import PageHeader from '~COMPONENTS/containers/PageHeader';
import { TranslatedCopy } from '../../intl/TranslatedCopy';
import { Filters } from '../../shop/filters/';
import { sortOptions } from './selectOptions';

import { getAllAccountCarts, setSelectedDepartment } from '../../carts/actions';
import { STATUS, getCarts, getCurrentCart } from '../../carts/constants';
import {
  getCurrentAllotments,
  getAllDepartments,
  getIsGreaterChinaSalesOrg,
  getIsShoppingEnabled
} from '../../gamedayAccount/selectors';
import { hasAuthCheckFinished } from '../../auth/reducer';
import { getUserAccount, getUserData } from '../../user/selectors';
import { setAdminCartId, toggleIsAdminCart } from '../../shop/actions';
import { filterList } from '../../util/filterList';
import { listIncludes } from '../../util/listIncludes';
import { findUnique } from '../../util/findUnique';
import { VIEW_TYPES } from '../../util/constants';
import { isViewOnly, isAdmin, isStandardOrAdmin } from '../../util/rolePermissions';
import { LABEL_SELECT_ALL } from '../../shop/constants';
import { modalTypes } from '../../types/modal';
import { userTypes, userAccountTypes, departmentsTypes } from '../../types/user';
import { newAllotmentTypes } from '../../types/allotments';
import { sortList } from '../../util/sortList';
import { isEqual } from '../../util/lodash';

/**
 * Constants were moved to above the class
 */
export const PENDING_CARTS = 'pending';
export const MY_CARTS = 'my carts';
export const ALL_CARTS = 'all carts';
const SEARCH_INPUT_NAME = 'openCarts__search';
export const DEPARTMENT_NAME_KEY = 'departmentName';
export const ORDER_TYPE_KEY = 'orderType';
export const SELECTED_FILTERS = () =>
  new Map([
    [DEPARTMENT_NAME_KEY, new Set()],
    [ORDER_TYPE_KEY, new Set()]
  ]);

export class OpenCarts extends Component {
  /**
   * @description Moved state to inside a constructor for consistent information
   * @param {*} props
   */
  constructor(props) {
    super(props);
    this.state = {
      cartListType: PENDING_CARTS,
      currentView: window.innerWidth < 1024 ? VIEW_TYPES.GRID : VIEW_TYPES.LIST,
      isFilterOpen: false,
      searchTerm: '',
      selectAllToggle: false,
      selectedFilters: SELECTED_FILTERS(),
      sortingBy: {
        desc: true,
        isNumber: false,
        name: new TranslatedCopy({
          id: 'openCarts.table.datePlaced'
        }).toString(),
        value: 'modifiedAt'
      }
    };
  }

  componentDidMount() {
    const { history, isShoppingEnabled } = this.props;
    if (!isShoppingEnabled) {
      history.push('/');
      return;
    }
    this.handleGetCarts();
  }

  componentDidUpdate(prevProps) {
    const { hasAuthCheckFinished, userAccount, carts, user } = this.props;
    if (
      (!prevProps.hasAuthCheckFinished && hasAuthCheckFinished) || // Auth check finished on page refresh
      (hasAuthCheckFinished && prevProps.userAccount?.number !== userAccount?.number) // User switched accounts
    ) {
      this.handleGetCarts();
    }

    if (!isEqual(prevProps.carts, carts)) {
      const cartListView = user?.role && !isViewOnly(user.role) ? PENDING_CARTS : MY_CARTS;
      this.updateCartListType(cartListView);
    }
  }

  setSearchTerm = searchTerm => this.setState({ searchTerm });

  tableDataByType = (carts, type) => {
    const { user } = this.props;
    const { cartListType: propsCartListType } = this.state;
    const cartListType = type || propsCartListType;

    switch (cartListType) {
      case PENDING_CARTS:
        return carts.filter(cart => cart.status === STATUS.PENDING);
      case ALL_CARTS:
        return carts.filter(cart => cart.status !== STATUS.SUBMITTED);
      case MY_CARTS:
      default:
        return carts.filter(
          cart => cart.createdBy.toLowerCase() === user.email.toLowerCase() && cart.status !== STATUS.SUBMITTED
        );
    }
  };

  /**
   * @description This is where we pull cart in and parse it to an array with department names.
   * @returns {Array<object>}
   */
  tableData = () => {
    const { carts, departments } = this.props;

    return carts.map(cart => {
      const {
        id,
        headers: { departmentNumber, orderType },
        createdBy,
        modifiedAt,
        status,
        totals: { wholesale: totalWholesale }
      } = cart;
      const department = departments[departmentNumber];

      return {
        id,
        departmentNumber,
        departmentName: (department && department.departmentName) || '',
        orderType,
        createdBy,
        modifiedAt,
        status,
        totalWholesale
      };
    });
  };

  /**
   * @description Sets the sortingBy state
   */
  handleSortCartList = sortingBy => this.setState({ sortingBy });

  /**
   * @description Expands the filter dropdown
   */
  handleFilterClick = () => {
    this.setState(prevState => ({ isFilterOpen: !prevState.isFilterOpen }));
  };

  /**
   * @description fetches all the carts.
   */
  handleGetCarts = () => {
    const { userAccount, getAllAccountCarts } = this.props;
    if (!userAccount || !userAccount.number) return;

    getAllAccountCarts(userAccount.number);
  };

  /**
   * @description Sets the state of cartListType and clearsFilters.
   */
  updateCartListType = cartListType => {
    this.setState({
      cartListType: cartListType
    });
    this.clearFilters();
  };

  /**
   * @description Resets the selectedFilters to a new object.
   */
  clearFilters = () => {
    this.setState({ selectedFilters: SELECTED_FILTERS() });
  };

  /**
   * @description This has been now cleaned up to be performed on cart load.
   */
  searchCartList = cartData => {
    const { searchTerm } = this.state;
    const normalizedVal = searchTerm && searchTerm !== '' ? searchTerm.toLowerCase() : null;
    if (normalizedVal) {
      const filterBy = [
        'name',
        'departmentNumber',
        DEPARTMENT_NAME_KEY,
        'createdBy',
        ORDER_TYPE_KEY,
        'modifiedAt',
        'totalWholesale'
      ];
      return filterList(cartData, filterBy, normalizedVal);
    }
    return cartData;
  };

  /**
   * @description gets the key and searches the carts for it. Then finds all the unique ones and sorts them.
   * @returns {Array<string>}
   */
  selectOptions = key => ({ key: key, values: findUnique(this.tableData(), key).sort() });

  /**
   * @description Handles the selection of all in the filter list when you click All.
   */
  handleSelectAllDepartments = (name, isChecked) => {
    const { selectedFilters, selectAllToggle } = this.state;
    const isSelectAllToggled = name === LABEL_SELECT_ALL && isChecked && isChecked !== selectAllToggle;
    this.tableData()
      .map(cart => cart.departmentName)
      .forEach(deptName => {
        if (isSelectAllToggled && !selectedFilters.get(DEPARTMENT_NAME_KEY).has(deptName)) {
          selectedFilters.get(DEPARTMENT_NAME_KEY).add(deptName);
        }
        if (!isSelectAllToggled && selectedFilters.get(DEPARTMENT_NAME_KEY).has(deptName)) {
          selectedFilters.get(DEPARTMENT_NAME_KEY).delete(deptName);
        }
      });
  };

  handleSelection = (name, isChecked) => {
    const { selectedFilters, selectAllToggle } = this.state;
    const isSelectAllToggled = name === LABEL_SELECT_ALL && isChecked && isChecked !== selectAllToggle;
    const [key, value] = name.split('--');

    /**
     * Check if name is the all label and if it is select all the departments
     */
    if (name === LABEL_SELECT_ALL) {
      this.handleSelectAllDepartments(name, isChecked);
    }

    /**
     * If there is a key and value it will then add or remove an item from the map.
     * This map is related to the selectedFilters map.
     */
    if (key && value) {
      if (isChecked) {
        selectedFilters.get(key).add(value);
      } else {
        selectedFilters.get(key).delete(value);
      }
    }

    /**
     * After each action then we apply state. This way we dont duplicate rerenders.
     */
    this.setState({
      selectedFilters,
      selectAllToggle: isSelectAllToggled
    });
  };

  /**
   * Sets the cart information
   */
  handleSetCart = (cartId, number, orderType) => {
    const { history, setAdminCartId, toggleIsAdminCart, setSelectedDepartment } = this.props;
    const { cartListType } = this.state;

    if (cartListType !== MY_CARTS) {
      const { cartId: propsCartId } = this.props;
      toggleIsAdminCart();
      setAdminCartId(propsCartId);
    }

    // TODO: If pending cart & isAdmin check if valid and go straight to review order
    setSelectedDepartment(number, orderType, cartId);
    history.push('/cart');
  };

  /**
   * @description Toggles the view between list || grid
   */
  handleToggleView = currentView => this.setState({ currentView });

  /**
   * @description Takes the cart information and sets the cart.
   */
  handleOpenCartClick = (cartId, number, orderType) => {
    // TODO: Add back in once we have cart validation from pending approval screen
    // const link = this.state.cartListType !== MY_CARTS ? '/review-order' : '/cart';
    this.handleSetCart(cartId, number, orderType);
  };

  /**
   * @description Takes the search term and sets it to an empty string
   */
  clearSearch = () => this.setState({ searchTerm: '' });

  /**
   * @description This takes all the information present in the class and applies it to the carts
   *
   * == It should only be applied in the render block! ==
   */
  filteredTableData = type => {
    const { sortingBy, searchTerm, selectedFilters } = this.state;
    const departments = Array.from(selectedFilters.get(DEPARTMENT_NAME_KEY));
    const orderType = Array.from(selectedFilters.get(ORDER_TYPE_KEY));

    // Gets the carts from props with department names and filters them based on type.
    let cartData = this.tableDataByType(this.tableData(), type);

    // Apply search terms to the returned carts.
    if (searchTerm.length >= 3) {
      cartData = this.searchCartList(cartData);
    }

    // Applies the filters for department name or orderType
    if (departments.length || orderType.length) {
      let normalizedVal = [];
      const filterBy = [];

      if (departments.length) {
        normalizedVal = departments;
        filterBy.push(DEPARTMENT_NAME_KEY);
      }

      if (orderType.length) {
        normalizedVal = [...orderType, ...normalizedVal];
        filterBy.push(ORDER_TYPE_KEY);
      }

      normalizedVal = normalizedVal.map(filter => {
        if (filter === 'at-once') {
          return 'atonce';
        }
        return filter;
      });

      cartData = listIncludes(cartData, filterBy, normalizedVal);
    }

    /**
     * Apply sortingBy and return the carts with sorting applied.
     */
    if (sortingBy && sortingBy.value) {
      return sortList(cartData, sortingBy.value, sortingBy.isNumber, !sortingBy.desc);
    }

    /**
     * Return carts if nothing was done.
     */
    return cartData;
  };

  /**
   * @description Simplified render for open cart search to allow reuse.
   */
  renderOpenCartSearch = (device, classNames) => (
    <div className={classnames(`status__search_container_${device || 'mobile'}`, classNames)}>
      <OpenCartsSearch onSubmit={this.setSearchTerm} clearSearch={this.clearSearch} name={SEARCH_INPUT_NAME} />
    </div>
  );

  /**
   * @description Simplified render of SortOptions to allow reuse.
   */
  renderSortOptions = () => {
    const { sortingBy } = this.state;
    return (
      <SortOptions
        sortBy={sortOptions}
        defaultValue={sortingBy}
        toggleView={this.handleToggleView}
        handleExternalSort={this.handleSortCartList}
      />
    );
  };

  /**
   * @description Filters, Sorting, and Searching is applied on render.
   */
  render() {
    const { currentAllotments, user, isGreaterChinaSalesOrg } = this.props;
    const { currentView, cartListType, isFilterOpen, selectedFilters, selectAllToggle } = this.state;
    const departmentOptions = this.selectOptions(DEPARTMENT_NAME_KEY);
    const orderTypeOptions = this.selectOptions(ORDER_TYPE_KEY);
    const filters = [departmentOptions, orderTypeOptions];
    const canSeePending = user ? isStandardOrAdmin(user.role) : false;
    const canSeeAll = user ? isAdmin(user.role) : false;
    const carts = this.filteredTableData(cartListType);
    return (
      <div className='openCarts status__main_outerWrapper'>
        <PageHeader
          page='cartApproval'
          title={new TranslatedCopy({ id: 'openCarts.page.title' }).toString()}
          className='flex-container row bg-white align-center'
        >
          <div className='flex-container row wrap align-center space-between'>
            <div className='status__main_menu u-full-height flex-container row align-center'>
              {canSeePending && (
                <Button
                  onClick={() => this.updateCartListType(PENDING_CARTS)}
                  className={classnames('ncss-brand fs14-sm d-sm-ib mr4-sm u-uppercase', {
                    selected: cartListType === PENDING_CARTS
                  })}
                >
                  <TranslatedCopy id='checkout.pending.title' />
                </Button>
              )}
              <Button
                onClick={() => this.updateCartListType(MY_CARTS)}
                className={classnames('ncss-brand fs14-sm d-sm-ib mr4-sm u-uppercase', {
                  selected: cartListType === MY_CARTS
                })}
              >
                <TranslatedCopy id='openCarts.my.carts' />
              </Button>
              {canSeeAll && (
                <Button
                  onClick={() => this.updateCartListType(ALL_CARTS)}
                  className={classnames('ncss-brand fs14-sm d-sm-ib u-uppercase', {
                    selected: cartListType === ALL_CARTS
                  })}
                >
                  <TranslatedCopy id='openCarts.all.carts' />
                </Button>
              )}
            </div>
            {this.renderOpenCartSearch('desktop', 'd-sm-h d-lg-ib')}
          </div>
        </PageHeader>
        {this.renderOpenCartSearch('mobile', 'd-lg-h bg-white mt12-sm p3-sm')}
        <div className='d-sm-b d-lg-h'>
          <div className='flex-container row space-between bg-white p4-sm'>
            <Button onClick={this.handleFilterClick}>
              <TranslatedCopy id='global.filter' />
            </Button>
            {this.renderSortOptions()}
          </div>
        </div>
        <div
          className={classnames('status__filters order-status-item pl8-md pr4-md flex-container', {
            isOpen: isFilterOpen
          })}
        >
          <Filters
            filters={filters}
            onChange={this.handleSelection}
            selected={selectedFilters}
            selectAll={selectAllToggle}
            pageName='openCarts'
            closeOnOutsideClick
            showFilterCount
          />
          <div className='d-sm-h d-lg-b flex-expand'>{this.renderSortOptions()}</div>
        </div>
        <div className='status__filters_bg' onClick={this.handleFilterClick} />
        <div className='p5-sm'>
          {carts.length > 0 && (
            <>
              {currentView === VIEW_TYPES.LIST ? (
                <div className='table-view fs12-sm pb4-md d-lg-b'>
                  <Table
                    data={carts}
                    columns={columns}
                    cellProps={{
                      handleOpenCartClick: this.handleOpenCartClick,
                      currencyCode: (currentAllotments && currentAllotments.currencyCode) || 'USD'
                    }}
                    isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
                  />
                </div>
              ) : (
                <div>
                  {carts.map((cart, idx) => (
                    <OpenCartCard
                      key={cart.id}
                      cart={cart}
                      idx={idx}
                      currencyCode={(currentAllotments && currentAllotments.currencyCode) || 'USD'}
                      handleOpenCartClick={this.handleOpenCartClick}
                      isGreaterChinaSalesOrg={isGreaterChinaSalesOrg}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          {!carts.length && <NoResults titleId='orderstatus.noresults.title' />}
        </div>
      </div>
    );
  }
}

OpenCarts.propTypes = {
  ...modalTypes,
  cartId: PropTypes.string,
  currentAllotments: newAllotmentTypes,
  departments: departmentsTypes,
  hasAuthCheckFinished: PropTypes.bool.isRequired,
  isGreaterChinaSalesOrg: PropTypes.bool.isRequired,
  setAdminCartId: PropTypes.func.isRequired,
  setSelectedDepartment: PropTypes.func.isRequired,
  toggleIsAdminCart: PropTypes.func.isRequired,
  user: userTypes,
  userAccount: userAccountTypes,
  isShoppingEnabled: PropTypes.func.isRequired
};

OpenCarts.defaultProps = {
  cartId: null,
  currentAllotments: null,
  departments: null,
  user: null,
  userAccount: null
};

const mapStateToProps = state => {
  const userAccount = getUserAccount(state);
  const carts = userAccount ? getCarts(state, userAccount.number) : [];

  return {
    cartId: getCurrentCart(state).id,
    carts,
    currentAllotments: getCurrentAllotments(state),
    departments: getAllDepartments(state),
    hasAuthCheckFinished: hasAuthCheckFinished(state),
    isGreaterChinaSalesOrg: getIsGreaterChinaSalesOrg(state),
    user: getUserData(state),
    userAccount,
    isShoppingEnabled: getIsShoppingEnabled(state)
  };
};

const connector = connect(mapStateToProps, {
  getAllAccountCarts,
  setAdminCartId,
  setSelectedDepartment,
  toggleIsAdminCart
});

export default connector(withRouter(OpenCarts));
